import { useCallback } from "react";

import { PlusIcon, XIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Button, Separator } from "twyne-new.ui";

import { ACTION } from "./utils";

export default function DropdownList({ definitions, data, onAction }) {
  return <DropdownMenu size='sm'>
    <DropdownMenuTrigger asChild className="float-right">
      <Button variant='light' size='sm' color='accent' className="w-fit">
        Add Field
        <PlusIcon size='14' />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className='min-w-70 max-h-100 overflow-y-auto' side='right'>
      {Object.entries(definitions).map(([group, props]) => <DropdownItems key={group}
        name={group} data={data} onAction={onAction} {...props} />)}
    </DropdownMenuContent>
  </DropdownMenu>
}

function DropdownItems({ name, fields, data, onAction, ...props }) {
  return <>
    <Group
      name={name}
      fields={fields}
      onAction={onAction}
      {...props}
    />
    {Object.entries(fields).map(([field, props]) => <Field
      key={field}
      data={data}
      name={field}
      onAction={onAction}
      group={name}
      {...props}
    />)}
    <Separator className="my-2" />
  </>
}

function Group({ label, name, fields, onAction }) {
  const handleAddAll = useCallback(event => {
    event.stopPropagation()
    onAction({ action: ACTION.ADD_ALL, group: name, fields })
  }, [name, fields, onAction])

  const handleRemoveAll = useCallback(event => {
    event.stopPropagation()
    onAction({ action: ACTION.REMOVE_ALL, group: name })
  }, [name, onAction])

  return <DropdownMenuItem className='group hover:bg-transparent! flex justify-between' >
    <span className="twy-text-base-accent text-lg">
      {label}
    </span>
    <GroupHover onAdd={handleAddAll} onRemove={handleRemoveAll} />
  </DropdownMenuItem>
}

function Field({ name, group, label, defaultValue, data, onAction }) {
  const isAdded = data[group]?.[name] !== undefined

  const handleAdd = useCallback(() => {
    onAction({ action: ACTION.ADD, group, field: name, value: defaultValue || undefined })
  }, [group, name, onAction, defaultValue])

  const handleRemove = useCallback(event => {
    event.stopPropagation()
    onAction({ action: ACTION.REMOVE, group, field: name })
  }, [group, name, onAction])

  const handleClick = event => {
    event.preventDefault()
    event.stopPropagation()
    if (!isAdded) {
      handleAdd()
    }
  }

  return <DropdownMenuItem
    onClick={handleClick}
    className={`flex justify-between group ${isAdded ? "cursor-default" : ""}`}
  >
    <span className={isAdded ? "twy-text-base-neutral" : ""}>
      {label}
    </span>
    <FieldHover isAdded={isAdded} onRemove={handleRemove} />
  </DropdownMenuItem>
}

function GroupHover({ onAdd, onRemove }) {
  return <div className='group-hover:opacity-100 opacity-0 transition-all'>
    <Button size='sm' variant='ghost' color='accent' onClick={onRemove}>
      <small>remove</small>
    </Button>
    <Button size='sm' variant='ghost' color='accent' onClick={onAdd}>
      <small>add</small>
    </Button>
  </div>
}

function FieldHover({ isAdded, onRemove }) {
  if (!isAdded) return null

  return <Button size='sm' variant='ghost' onClick={onRemove} className='opacity-0 group-hover:opacity-100 transition-all'>
    <XIcon size="14" />
  </Button>
}
